import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { extractPureTextFromRichText } from 'seagull';
import '~/components/AgreementStudioPage/TemplateVariables/TemplateVariableBlot/TemplateVariableBlot';
import { useResponsive } from '~/client-flow/contexts';
import { buildSystemVariableResolution } from '~/components/AgreementStudioPage/TemplateVariables/TemplateVariables.utils';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { AgreementDataModel, relationshipModel } from '~/models';
import { ServiceCostType } from '~/models/service-cost';
import { toDate } from '~/models/time/translator';
import { utils } from '~/utils';
import { getFutureBillingText } from '~/utils/service-description/getFutureBillingText';
import { bulletPointsTextsPath, getServiceBulletPoints } from '~/utils/service-description/getServiceBulletPoints';
import { billingBadgesTextsPath, billingDetailsBadge, discountBadge as getDiscountBadge } from '~/utils/service-description/getServiceDescriptionBadges';
import { renderServicePrice } from '~/utils/service-description/renderServicePrice';
import { ServiceDescription } from '../../../components/ServiceDescription/ServiceDescription';
import { DesktopServiceItem } from './DesktopServiceItem';
import { MobileServiceItem } from './MobileServiceItem';
import { GetServiceItemTID } from './ServiceItem.types';
export const ServiceItem = (param)=>{
    let { service, relationship, now, isOptional, isSelected, onToggle, testId } = param;
    var _service_bundle, _service_bundle1;
    const { ServiceItemTestIds } = useMemo(()=>GetServiceItemTID(testId), [
        testId
    ]);
    const featureFlags = useFeatureFlags();
    const { isMobile } = useResponsive();
    const { t: billingBadgesTexts } = useLocaleKeys(billingBadgesTextsPath);
    const { t: bulletPointsTexts } = useLocaleKeys(bulletPointsTextsPath);
    const { t: calloutsTexts } = useLocaleKeys('billing.callouts');
    const { t: reviewTexts } = useLocaleKeys('clientFlow.reviewServices');
    const agreementData = useMemo(()=>AgreementDataModel.fromRelationship(relationship), [
        relationship
    ]);
    const discountBadgeData = getDiscountBadge(service, billingBadgesTexts);
    const getBulletPoints = (skin)=>getServiceBulletPoints({
            service,
            now,
            agreementData,
            isReadOnly: true,
            skin,
            variant: 'paragraph2',
            weight: 'light',
            featureFlags,
            texts: bulletPointsTexts
        });
    const getBillingBadgeText = ()=>billingDetailsBadge(service, {
            t: billingBadgesTexts,
            effectiveDate: agreementData.effectiveDate,
            isClientPreview: true,
            now
        }).text;
    const futureBillingText = getFutureBillingText(service, now, calloutsTexts);
    const { CLIENT_ONBOARDING_NEW_FLOW } = featureFlags;
    const resolvedValues = useMemo(()=>{
        var _relationship_buyer_firstName, _relationship_buyer_lastName;
        return buildSystemVariableResolution({
            clientCompanyName: relationshipModel.customerName(relationship),
            clientFirstName: (_relationship_buyer_firstName = relationship.buyer.firstName) !== null && _relationship_buyer_firstName !== void 0 ? _relationship_buyer_firstName : undefined,
            clientLastName: (_relationship_buyer_lastName = relationship.buyer.lastName) !== null && _relationship_buyer_lastName !== void 0 ? _relationship_buyer_lastName : undefined,
            clientEmail: relationship.buyer.email,
            vendorBusinessName: relationship.vendor.name,
            agreementName: relationship.title,
            effectiveDate: agreementData.effectiveDate ? toDate(agreementData.effectiveDate) : null,
            currentDate: now
        });
    }, [
        relationship,
        agreementData.effectiveDate,
        now
    ]);
    const renderPrice = ()=>renderServicePrice(service, reviewTexts);
    const getSubtitlePrice = ()=>{
        switch(service.cost.pricingType){
            case ServiceCostType.Fixed:
            case ServiceCostType.Variable:
                return utils.formatDollarAmount(utils.applyDiscount(service.cost.price, service.cost.discount));
            case ServiceCostType.Range:
                {
                    const minFormatted = service.cost.minPrice && !service.cost.minPrice.eq(0) ? "".concat(utils.formatDollarAmount(utils.applyDiscount(service.cost.minPrice, service.cost.discount)), " - ") : reviewTexts.upTo();
                    return "".concat(minFormatted).concat(utils.formatDollarAmount(utils.applyDiscount(service.cost.maxPrice, service.cost.discount)));
                }
        }
    };
    const hasIncludedServices = ((_service_bundle = service.bundle) === null || _service_bundle === void 0 ? void 0 : _service_bundle.services) && service.bundle.services.length > 0;
    const hasBillingDetails = getBulletPoints('secondary').bullets.length > 0;
    const hasDescription = !!service.description && extractPureTextFromRichText(service.description).trim().length > 0;
    const includedServicesWithDescription = useMemo(()=>{
        var _ref;
        var _service_bundle;
        return (_ref = (_service_bundle = service.bundle) === null || _service_bundle === void 0 ? void 0 : _service_bundle.services.filter((s)=>s.description && extractPureTextFromRichText(s.description).trim().length > 0)) !== null && _ref !== void 0 ? _ref : [];
    }, [
        (_service_bundle1 = service.bundle) === null || _service_bundle1 === void 0 ? void 0 : _service_bundle1.services
    ]);
    const hasIncludedServiceDescriptions = includedServicesWithDescription.length > 0;
    const hasExpandableContent = hasDescription || hasIncludedServices || hasIncludedServiceDescriptions || hasBillingDetails;
    const getServiceDescription = (options)=>/*#__PURE__*/ _jsx(ServiceDescription, {
            service: service,
            relationship: relationship,
            now: now,
            resolvedValues: resolvedValues,
            hideIncludedServices: options === null || options === void 0 ? void 0 : options.hideIncludedServices,
            includedServiceDescriptionsTestId: ServiceItemTestIds.IncludedServiceDescriptions(),
            billingDetailsTestId: ServiceItemTestIds.BillingDetails()
        });
    const sharedProps = {
        service,
        isOptional,
        isSelected,
        onToggle,
        discountBadgeData,
        getBillingBadgeText,
        futureBillingText,
        useInlineFutureBilling: CLIENT_ONBOARDING_NEW_FLOW,
        renderPrice,
        getSubtitlePrice,
        hasIncludedServices: !!hasIncludedServices,
        hasExpandableContent: !!hasExpandableContent,
        getServiceDescription,
        ServiceItemTestIds,
        resolvedValues
    };
    if (isMobile) {
        return /*#__PURE__*/ _jsx(MobileServiceItem, _object_spread({}, sharedProps));
    }
    return /*#__PURE__*/ _jsx(DesktopServiceItem, _object_spread({}, sharedProps));
};
